import { Container, Box, Typography, Divider, Link, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPANY, companyFullAddress } from "../config/companyInfo";

// Srpska verzija je merodavna; engleska je informativni prevod (URL prijavljen
// prodavnicama kao stranica za brisanje naloga mora ostati isti za oba jezika).
export default function AccountDeletionPage() {
    const { i18n } = useTranslation();
    const isEn = (i18n.resolvedLanguage || i18n.language || "sr").startsWith("en");
    return isEn ? <DeletionEn /> : <DeletionSr />;
}

function DeletionSr() {
    const navigate = useNavigate();

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="md">
                <Box sx={{ bgcolor: "white", borderRadius: 3, p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                        Brisanje naloga i podataka
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Kako da obrišete svoj {COMPANY.brandName} nalog i podatke povezane sa njim.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Section title="1. Brisanje naloga iz aplikacije">
                        U {COMPANY.brandName} mobilnoj aplikaciji: <b>Profil → „Obriši nalog"</b>. Time se
                        trajno briše Vaš nalog (identitet za prijavu — e-mail i lozinka i/ili veza sa
                        Google nalogom). Radnja se ne može opozvati.
                        {"\n"}Ako ste nalog napravili na sajtu, brisanje zatražite postupkom iz tačke 2 —
                        rezultat je isti.
                    </Section>

                    <Section title="2. Zahtev za brisanje bez aplikacije">
                        Pošaljite zahtev sa e-mail adrese Vašeg naloga na {COMPANY.supportEmail}, sa
                        naslovom „Brisanje naloga". Zahtev obrađujemo u zakonskom roku i o ishodu Vas
                        obaveštavamo na istu adresu. Instalacija aplikacije nije potrebna.
                    </Section>

                    <Section title="3. Koji podaci se brišu">
                        Brišu se podaci naloga i lični podaci koje smo prikupili: e-mail adresa, jedinstveni
                        identifikator naloga i (ako je unet) ime.
                    </Section>

                    <Section title="4. Koji podaci se zadržavaju i koliko">
                        Izdate karte i fiskalni računi mogu se zadržati u zakonski propisanom roku radi
                        ispunjenja poreskih i računovodstvenih obaveza (fiskalizacija), i posle brisanja
                        naloga. Ovi podaci se ne koriste ni u koju drugu svrhu. Detalji su u{" "}
                        <Link onClick={() => navigate("/politika-privatnosti")} sx={{ cursor: "pointer", fontWeight: 700 }}>
                            Politici privatnosti
                        </Link>.
                    </Section>

                    <Section title="5. Kontakt">
                        {COMPANY.legalName}
                        {"\n"}Adresa: {companyFullAddress}
                        {"\n"}Podrška (zahtevi za brisanje): {COMPANY.supportEmail}, {COMPANY.supportPhone}
                        {"\n"}Sedište: {COMPANY.email}, {COMPANY.phone}
                    </Section>
                </Box>
            </Container>
        </Box>
    );
}

function DeletionEn() {
    const navigate = useNavigate();

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="md">
                <Box sx={{ bgcolor: "white", borderRadius: 3, p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                        Account and Data Deletion
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        How to delete your {COMPANY.brandName} account and the data associated with it.
                    </Typography>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        This English translation is provided for convenience only. In case of any
                        discrepancy, the Serbian version prevails.
                    </Alert>
                    <Divider sx={{ mb: 3 }} />

                    <Section title="1. Deleting the account from the app">
                        In the {COMPANY.brandName} mobile app: <b>Profile → “Delete account”</b>. This
                        permanently deletes your account (your sign-in identity — e-mail and password and/or
                        the link to your Google account). This action cannot be undone.
                        {"\n"}If you created your account on the website, request deletion using the procedure
                        in Section 2 — the result is the same.
                    </Section>

                    <Section title="2. Deletion request without the app">
                        Send a request from your account's e-mail address to {COMPANY.supportEmail}, with the
                        subject “Account deletion”. We process the request within the statutory period and
                        notify you of the outcome at the same address. Installing the app is not required.
                    </Section>

                    <Section title="3. What data is deleted">
                        Account data and the personal data we have collected are deleted: e-mail address, the
                        unique account identifier and (if provided) name.
                    </Section>

                    <Section title="4. What data is retained and for how long">
                        Issued tickets and fiscal receipts may be retained for the statutory period in order to
                        fulfil tax and accounting obligations (fiscalisation), even after account deletion.
                        This data is not used for any other purpose. Details are available in the{" "}
                        <Link onClick={() => navigate("/politika-privatnosti")} sx={{ cursor: "pointer", fontWeight: 700 }}>
                            Privacy Policy
                        </Link>.
                    </Section>

                    <Section title="5. Contact">
                        {COMPANY.legalName}
                        {"\n"}Address: {companyFullAddress}
                        {"\n"}Support (deletion requests): {COMPANY.supportEmail}, {COMPANY.supportPhone}
                        {"\n"}Head office: {COMPANY.email}, {COMPANY.phone}
                    </Section>
                </Box>
            </Container>
        </Box>
    );
}

function Section({ title, children }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: "pre-line" }}>
                {children}
            </Typography>
        </Box>
    );
}
